<template>
    <section>
        <form
            :class="{ 'padded-container': isEditPayment }"
            class="payment-layout__container"
            ref="paymentForm"
            v-on:submit.stop.prevent>
            <div
                class="aa-layout payment-layout--edit"
                v-bind:class="[
                    { 'account-grid': isSwitchPlan || isEditPayment },
                    { 'payment-summary__layout': enhancedPaymentSummaryEnabled },
                    { '-no-steps-header': !showStepsHeader },
                    { '-intl-single-col': isEditPayment && intlSingleColumnPaymentPageEnabled },
                ]"
                :aa-region="formConfig.aaRegion">
                <div
                    v-if="
                        isEditPayment && !isFromPaymentSummary && intlSingleColumnPaymentPageEnabled
                    "
                    class="grid-item grid-item--payment-row grid-item--row-bordered sub-page--header aa-layout">
                    <h3>{{ $getLocale('account_payment') }}</h3>
                </div>
                <!-- tabs menu -->
                <PaymentTabsContainer
                    :activeTab="activeTab"
                    :isEditPayment="isEditPayment"
                    :pageTypeForTracking="pageTypeForTracking"
                    @update:activeTab="emitActiveTab($event)" />
                <!-- START OF AB TEST PLOCTOPLUS-1915: center alignment payment page -->
                <!-- payment form tab (credit card/paypal): no AB Test, center alignment payment page AB TEST, control, variants A and B -->
                <transition-group
                    name="fade"
                    tag="div"
                    :class="[
                        { ...getPaymentFormTabsModuleClass },
                        { '-no-steps-header': !showStepsHeader },
                    ]">
                    <PaymentCreditCardTab
                        :key="tab.cc"
                        :activeTab="activeTab"
                        :isEditPayment="isEditPayment"
                        :isFromPaymentSummary="isFromPaymentSummary"
                        :formReference="$refs.paymentForm"
                        :handleProcessedPaymentError="handleProcessedPaymentError"
                        :handleProcessedPayment="handleProcessedPayment"
                        :isFTC="isFTC"
                        v-bind:formIsValid.sync="formIsValid"
                        :errorMessageElementId="errorMessageElementId" />
                    <PaymentPaypalTab
                        :key="tab.payPal"
                        :activeTab="activeTab"
                        :isEditPayment="isEditPayment" />
                </transition-group>

                <input type="hidden" name="recurly-token" data-recurly="token" />
                <div
                    v-if="showPaypalCheckout"
                    class="grid-item grid-item--row sub-page--actionbar sub-page--actionbar-padded">
                    <button
                        class="payment-layout__summary__submit paypal-checkout qt-paypalbtn"
                        @click="handlePayPalCheckout($event, paypalGateway)">
                        <span class="label">
                            {{ $getLocale('checkout_with') }}
                        </span>
                        <span class="logo" aria-label="PayPal"></span>
                    </button>
                </div>
                <div
                    v-if="isEditPayment"
                    data-testid="edit-payment-actions"
                    class="grid-item grid-item--row sub-page--actionbar sub-page--actionbar-padded">
                    <ButtonCta
                        v-if="showCancelButton && intlSingleColumnPaymentPageEnabled"
                        data-testid="edit-payment-cancel"
                        :buttonConfig="cancelConfig" />
                    <template v-if="activeTab === tab.cc">
                        <FormButton
                            :buttonConfig="buttonConfig"
                            :buttonState="submitButtonState"
                            @click="onClickSubmit" />
                    </template>
                    <template v-else-if="activeTab === tab.payPal">
                        <button
                            class="account-payment-layout__summary__submit paypal-checkout qt-paypalbtn"
                            @click="handlePayPalCheckout($event, paypalGateway)">
                            <span class="paypal-label">{{ $getLocale('switch_to') }}</span>
                            <span class="logo" aria-label="PayPal"></span>
                        </button>
                    </template>
                </div>
            </div>
        </form>
        <transition name="fade">
            <WaitingOverlay
                :line1="getPaymentProcessingMessage"
                :line2="$getLocale('this_will_not_take_long')"
                v-if="formIsProcessing && intlSingleColumnPaymentPageEnabled" />
        </transition>
        <div class="threeDSecureModal" v-bind:class="{ open: threeDModalOpen }">
            <div class="backdrop"></div>
            <div class="container" ref="threeDSecure"></div>
        </div>
    </section>
</template>

<script>
    import { mapGetters, mapMutations } from 'vuex';
    import { SKIP_EXPLAINER_STEPS, SINGLE_COLUMN_PAYMENT_PAGE } from 'aa/helpers/featureConstants';
    import PaymentCreditCardTab from './PaymentCreditCardTab';
    import PaymentPaypalTab from './PaymentPaypalTab';
    import PaymentTabsContainer from './PaymentTabsContainer';
    import { CreditCardPaymentConfig, TAB } from 'aa/vue/FormConfig';
    // START TEST PLOCTOPLUS-1915: center align payment page
    import CenterAlignPaymentPageAbTest from 'aa/vue/abTestRelated/CenterAlignPaymentPageAbTest';
    // END TEST PLOCTOPLUS-1915: center align payment page
    import checkoutMixin from 'aa/vue/plugins/checkoutMixin';
    import FormButton from 'atoms/FormButton';
    import ButtonCta from 'atoms/ButtonCta';
    import WaitingOverlay from 'aa/vue/atoms/WaitingOverlay';
    import FormState from 'aa/vue/plugins/FormState';
    import { ACTION_NAMES, PAGE_TYPES, tracking } from 'services/Tracking';
    import NotificationBarModel from 'aa/vue/models/NotificationBarModel';
    import { FTC_COMPLIANCE_ENABLED } from 'helpers/featureConstants';
    import { FLOW_TYPE } from 'models/FlowModel';
    import { RECAPTCHA_FORMS } from 'recaptcha/Recaptcha';
    import { setUpThreeDSecure } from './signupUtil';
    import { PAYMENT_CC, PAYMENT_PAYPAL } from 'aa/vue/constants/shared';

    export default {
        name: 'PaymentMethodSelection',
        mixins: [CenterAlignPaymentPageAbTest, FormState, checkoutMixin],
        components: {
            PaymentCreditCardTab,
            PaymentPaypalTab,
            PaymentTabsContainer,
            FormButton,
            ButtonCta,
            WaitingOverlay,
        },
        props: {
            setUpThreeDSecure: {
                type: Function,
                default: setUpThreeDSecure,
            },
            isEditPayment: {
                type: Boolean,
                default: false,
            },
            showCancelButton: {
                type: Boolean,
                default: false,
            },
            isFromPaymentSummary: {
                type: Boolean,
                default: false,
            },
            handleProcessedPaymentError: {
                type: Function,
                default: () => {},
            },
            handleProcessedPayment: {
                type: Function,
                default: () => {},
            },
            isFTC: {
                type: Boolean,
                default: false,
            },
            errorMessageElementId: {
                type: String,
                default: null,
            },
            pageTypeForTracking: {
                type: String,
                default: '',
            },
        },
        data: function () {
            return {
                enhancedPaymentSummaryEnabled: false,
                featurePaypal: false,
                acceptedCards: [],
                activeTab: TAB.cc,
                countryFormClass: '',
                altPaymentFormView: false,
                payPalToken: null,
                recaptchaAction: RECAPTCHA_FORMS.FORM_PAYMENT,
                formIsProcessing: false,
                formIsValid: false,
            };
        },
        mounted() {
            this.enhancedPaymentSummaryEnabled =
                this.$store.state.serverData.enhancedPaymentSummaryEnabled;
            this.featurePaypal = this.$store.state.serverData.paymentMethods?.paypal?.enabled;
            if (this.isEditPayment) {
                this.recaptchaAction = RECAPTCHA_FORMS.FORM_EDIT_PAYMENT;
            }
            this.fieldConfigs = this.$store.getters['ftc/getFieldConfigs'];
        },
        computed: {
            ...mapGetters(['featureIsActive']),
            ...mapGetters('plan', ['isSwitchPlan']),
            ...mapGetters('ftc', ['getPayPalToken']),
            hasSkipExplainers() {
                return this.featureIsActive(SKIP_EXPLAINER_STEPS);
            },
            intlSingleColumnPaymentPageEnabled() {
                return this.featureIsActive(SINGLE_COLUMN_PAYMENT_PAGE);
            },
            showStepsHeader() {
                if (this.$store.state.serverData.hideStepsHeader) {
                    return false;
                }

                return !this.isEditPayment && !this.hasSkipExplainers;
            },
            showPaypalCheckout() {
                return (
                    this.activeTab === this.tab.payPal &&
                    !this.paypalTokenIsPresent &&
                    !this.isEditPayment
                );
            },
            tab() {
                return TAB;
            },
            cancelConfig() {
                return {
                    type: 'button',
                    styleClass: ['button', 'secondary'],
                    displayText: this.$getLocale('cancel'),
                    event: this.cancelOnClicked,
                };
            },
            formConfig() {
                return CreditCardPaymentConfig;
            },
            paypalGateway() {
                if (this.featurePaypal) {
                    const appMode = this.$store.state.serverData?.appMode;
                    const defaultGatewayList =
                        this.$store.state.serverData.paymentMethods.paypal?.gateway?.default;
                    if (appMode) {
                        return defaultGatewayList ? defaultGatewayList[appMode] : false;
                    }
                    return false;
                }
                return false;
            },
            paypalTokenIsPresent() {
                return this.getPayPalToken?.id;
            },
            trackingService() {
                return this.$trackingService ?? tracking;
            },
            isFTCComplianceEnabled() {
                return this.featureIsActive(FTC_COMPLIANCE_ENABLED);
            },
            getPaymentProcessingMessage() {
                return this.isEditPayment
                    ? this.$getLocale('we_are_updating_your_payment_method')
                    : this.$getLocale('we_are_processing_your_subscription');
            },
        },
        methods: {
            ...mapMutations('ftc', ['setFormValidated']),
            emitActiveTab(tab) {
                this.activeTab = tab;
                this.$emit('update:activeTab', tab);
            },

            cancelOnClicked() {
                this.trackingService.trackAction(ACTION_NAMES.CREDIT_CARD_CANCEL);
                this.$router.push({ name: 'AccountMain' });
            },
            submitEditPayment(token) {
                const payload = {
                    token: token.id,
                };

                this.paymentRequestData = payload;
                const httpData = this.buildHttpData(payload);
                this.sendPayment(httpData);
            },

            submitPayment(token, buttonEvent) {
                this.formErrorMessage = null;
                this.formIsProcessing = true;

                let couponCode = null;
                if (this.coupon && this.coupon.recurlyInfo && this.coupon.recurlyInfo.code) {
                    couponCode = this.coupon.recurlyInfo.code;
                }

                let payload = {
                    token: token,
                    buttonEvent: buttonEvent,
                    plan: this.currentPlan,
                    couponCode: couponCode,
                    tk_trp: this.$store.state.token,
                };

                if (this.flowType === FLOW_TYPE.AARP) {
                    let aarpUserId = { aarpUserId: this.getAarpUserId() };
                    Object.assign(payload, aarpUserId);
                }

                // Save payload for retry if needed
                this.paymentRequestData = payload;

                this.isEditPayment &&
                    this.trackingService.trackAction(ACTION_NAMES.CREDIT_CARD_SUBMIT_UPDATE);
                this.sendPayment(payload);
            },

            sendPayment(payload) {
                this.formIsProcessing = true;

                if (
                    this.hasOwnProperty('recaptchaAction') &&
                    this.recaptchaFormActionEnabled(this.recaptchaAction)
                ) {
                    payload.recaptchaAction = this.recaptchaAction;
                }

                const couponCode = payload.couponCode;
                if ('URLSearchParams' in window) {
                    if (location.search) {
                        const searchParams = new URLSearchParams(location.search.substr(1));
                        const gatewayOverride = searchParams.get('gateway');
                        if (gatewayOverride) {
                            payload.gateway = gatewayOverride;
                        }
                    }
                }

                const processSelectedMethod = this.isEditPayment
                    ? this.processEditPayment
                    : this.processPayment;
                processSelectedMethod(payload)
                    .then(async (resp) => {
                        if (!this.isFromPaymentSummary) {
                            this.handleProcessedPayment(resp);
                        }

                        if (!resp.success || !this.isEditPayment) {
                            return;
                        }

                        const purchasePaymentMethod =
                            this.activeTab === this.tab.cc ? PAYMENT_CC : PAYMENT_PAYPAL;

                        if (this.isFromPaymentSummary) {
                            await this.syncServerDataWithDeepAssign();
                            this.formIsProcessing = false;
                            this.editPaymentSuccess = true;
                            this.trackingService.trackAction(
                                ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                {
                                    ctaText: this.buttonConfig.displayText,
                                    pageURL: window.location.href,
                                    pageType: PAGE_TYPES.PLAN_CHANGE_CONFIRMATION,
                                    purchasePaymentMethod,
                                },
                            );

                            // Timeout to allow the button success state to be displayed before closing the form in the payment summary page
                            setTimeout(() => {
                                this.$emit('update-payment-method-success', resp.success);
                                this.editPaymentSuccess = false;
                            }, 1000);

                            this.openNotification(
                                new NotificationBarModel({
                                    success: true,
                                    message: this.$getLocale(
                                        'your_payment_information_was_successfully_updated',
                                    ),
                                }),
                            );
                        }

                        if (!this.isFromPaymentSummary) {
                            this.trackingService.trackAction(
                                ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                {
                                    linkName: ACTION_NAMES.TRACK_UPDATE_PAYMENT_METHOD_SUCCESS,
                                    pageType: PAGE_TYPES.ACCOUNT_UPDATE_PAYMENT,
                                    pageURL: window.location.href,
                                    pageName: '/account/payment',
                                    purchasePaymentMethod,
                                    optimizelyExp: CBS.Tracking?.utag_data?.optimizelyExp,
                                    userType: this.$store.state.user?.svod?.status,
                                    userStatus: this.$store.getters['user/trackingUserStatus'],
                                    userProfileId: CBS.Registry?.user?.profile_id,
                                    userProfileCategory: CBS.Registry?.user?.profile_type,
                                    userProfilePicPath: CBS.Registry?.user?.userProfilePicPath,
                                    userRegId: this.$store.state?.user?.regID,
                                },
                            );
                        }
                    })
                    .catch((err) => {
                        let trackingParams = {};
                        if (this.activeTab === TAB.payPal) {
                            const { planType } = this.plan;
                            trackingParams.purchasePaymentMethod = this.activeTab;
                            trackingParams.productPricingPlan = planType;
                        }
                        this.handleProcessedPaymentError(err, trackingParams);
                    })
                    .finally(() => {
                        if (this.formIsProcessing) {
                            this.formIsProcessing = false;
                        }
                    });
            },
        },
        watch: {
            activeTab(newValue) {
                this.$emit('update-active-tab', newValue);
            },
            formIsValid(newValue) {
                this.setFormValidated(newValue);
                this.$store.commit('payment/setFormReference', this.$refs.paymentForm);
            },
        },
    };
</script>
